import { supabase } from '../lib/supabase';
import type { OrganizationStats } from '../types';

export interface CreateOrganizationParams {
  organizationName: string;
  adminEmail: string;
  adminFirstName?: string | null; 
  adminLastName?: string | null; 
  totalSeats?: number;
}

export async function createOrganizationAndInviteAdmin(params: CreateOrganizationParams) {
  const { data, error } = await supabase.rpc('create_organization_and_invite_first_admin', {
    p_organization_name: params.organizationName,
    p_admin_email: params.adminEmail,
    p_admin_first_name: params.adminFirstName || null,
    p_admin_last_name: params.adminLastName || null,
    p_total_seats: params.totalSeats || 5
  });

  if (error) throw error;
  return data;
}

export async function getAllOrganizationsStats(): Promise<OrganizationStats[]> {
  const { data, error } = await supabase.rpc('get_all_organizations_stats');

  if (error) throw error;
  return data || [];
}

export async function deleteOrganization(organizationId: string) {
  const { data, error } = await supabase.rpc('delete_organization', {
    p_organization_id: organizationId
  });

  if (error) throw error;
  return data;
}

export async function sendInvitationEmail(params: {
  invitationId: string;
  token: string;
  organizationName: string;
  adminName?: string;
  recipientEmail: string;
}) {
  // Log the parameters to help with debugging
  console.log('Sending invitation with params:', JSON.stringify(params));
  
  // Validate required parameters
  if (!params.invitationId || !params.token || !params.organizationName || !params.recipientEmail) {
    throw new Error('Missing required parameters');
  }

  const response = await fetch(`${supabase.supabaseUrl}/functions/v1/send-invitation-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabase.supabaseKey}`
    },
    body: JSON.stringify(params)
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to send invitation email');
  }

  return await response.json();
}

export async function isSuperAdmin(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('is_super_admin', {
      user_uuid: (await supabase.auth.getUser()).data.user?.id
    });

    if (error) throw error;
    return !!data;
  } catch (err) {
    console.error('Error checking super admin status:', err);
    return false;
  }
}